import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import type { EventRow, EventRsvp, EventGuest } from "./types";

/**
 * Loads a single event + the current user's RSVP + (if host) the full guest
 * list for check-in. Provides RSVP create/cancel + per-guest check-in toggle.
 *
 * Capacity / waitlist semantics:
 * - `event.capacity` is total tickets (primary attendees + plus-ones).
 * - When the requested status is "going" but the event is full, the RSVP is
 *   created/updated as "waitlist" and the caller is told via `promoted: false`.
 * - When a "going" RSVP is cancelled, the head of the waitlist (oldest
 *   created_at) is promoted to "going" — best-effort, fire-and-forget if the
 *   user can't write (RLS will silently no-op).
 */
export const useEvent = (eventId: string | undefined) => {
  const { user } = useAuth();
  const [event, setEvent] = useState<EventRow | null>(null);
  const [myRsvp, setMyRsvp] = useState<EventRsvp | null>(null);
  const [myGuests, setMyGuests] = useState<EventGuest[]>([]);
  const [allRsvps, setAllRsvps] = useState<EventRsvp[]>([]);
  const [allGuests, setAllGuests] = useState<EventGuest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!eventId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    const { data: ev, error: eErr } = await supabase
      .from("events")
      .select("*")
      .eq("id", eventId)
      .maybeSingle();
    if (eErr) setError(eErr.message);
    setEvent((ev as EventRow | null) ?? null);

    const isHost = !!user && !!ev && (ev as any).host_user_id === user.id;

    if (user) {
      const { data: rsvp } = await supabase
        .from("event_rsvps")
        .select("*")
        .eq("event_id", eventId)
        .eq("user_id", user.id)
        .maybeSingle();
      setMyRsvp((rsvp as EventRsvp | null) ?? null);

      if (rsvp) {
        const { data: gs } = await supabase
          .from("event_guests")
          .select("*")
          .eq("rsvp_id", (rsvp as any).id);
        setMyGuests((gs as EventGuest[]) ?? []);
      } else {
        setMyGuests([]);
      }
    }

    if (isHost) {
      const { data: rsvps } = await supabase
        .from("event_rsvps")
        .select("*")
        .eq("event_id", eventId)
        .order("created_at", { ascending: true });
      const rsvpList = (rsvps as EventRsvp[]) ?? [];
      setAllRsvps(rsvpList);
      const ids = rsvpList.map((r) => r.id);
      if (ids.length) {
        const { data: gs } = await supabase
          .from("event_guests")
          .select("*")
          .in("rsvp_id", ids);
        setAllGuests((gs as EventGuest[]) ?? []);
      } else {
        setAllGuests([]);
      }
    } else {
      setAllRsvps([]);
      setAllGuests([]);
    }

    setLoading(false);
  }, [eventId, user]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Capacity probe — counts confirmed tickets across ALL going RSVPs for this
   * event so non-host members can know if "going" will be auto-waitlisted.
   * Uses a head-only count via guests joined on RSVPs.
   */
  const probeCapacity = useCallback(async (): Promise<{
    capacity: number | null;
    going_tickets: number;
    seats_left: number | null;
  }> => {
    if (!event) return { capacity: null, going_tickets: 0, seats_left: null };
    if (event.capacity == null) {
      return { capacity: null, going_tickets: 0, seats_left: null };
    }
    const { data: goingRsvps } = await supabase
      .from("event_rsvps")
      .select("id")
      .eq("event_id", event.id)
      .eq("status", "going");
    const ids = ((goingRsvps as { id: string }[] | null) ?? []).map((r) => r.id);
    if (ids.length === 0) {
      return { capacity: event.capacity, going_tickets: 0, seats_left: event.capacity };
    }
    const { count } = await supabase
      .from("event_guests")
      .select("id", { count: "exact", head: true })
      .in("rsvp_id", ids);
    const going = count ?? 0;
    return {
      capacity: event.capacity,
      going_tickets: going,
      seats_left: Math.max(0, event.capacity - going),
    };
  }, [event]);

  const rsvp = useCallback(
    async (
      status: "going" | "waitlist" | "declined",
      guestNames: string[] = [],
    ): Promise<
      | { error: string }
      | { ok: true; status: "going" | "waitlist" | "declined"; promoted: boolean }
    > => {
      if (!user || !eventId) return { error: "Not signed in" };

      // Translate a "going" request to "waitlist" if the event is full.
      let effectiveStatus = status;
      let autoWaitlisted = false;
      if (status === "going") {
        const probe = await probeCapacity();
        if (probe.capacity != null) {
          const requested = 1 + guestNames.length;
          if (probe.seats_left !== null && probe.seats_left < requested) {
            effectiveStatus = "waitlist";
            autoWaitlisted = true;
          }
        }
      }

      let rsvpId = myRsvp?.id;
      if (!rsvpId) {
        const { data, error } = await supabase
          .from("event_rsvps")
          .insert({
            event_id: eventId,
            user_id: user.id,
            status: effectiveStatus,
          } as any)
          .select("*")
          .maybeSingle();
        if (error) return { error: error.message };
        rsvpId = (data as any)?.id;
      } else {
        const { error } = await supabase
          .from("event_rsvps")
          .update({ status: effectiveStatus })
          .eq("id", rsvpId);
        if (error) return { error: error.message };
      }

      // Only mint guest rows when actually going. Waitlist entries skip QR
      // generation until promoted.
      if (rsvpId && effectiveStatus === "going") {
        const { data: existing } = await supabase
          .from("event_guests")
          .select("id")
          .eq("rsvp_id", rsvpId);
        const have = (existing as { id: string }[] | null)?.length ?? 0;
        const target = 1 + guestNames.length;
        if (have < target) {
          const toInsert: { rsvp_id: string; guest_name: string | null; checked_in: boolean }[] = [];
          if (have === 0) {
            toInsert.push({ rsvp_id: rsvpId, guest_name: null, checked_in: false });
          }
          for (const name of guestNames) {
            toInsert.push({ rsvp_id: rsvpId, guest_name: name, checked_in: false });
          }
          if (toInsert.length) {
            await supabase.from("event_guests").insert(toInsert as any);
          }
        }
      }

      await load();
      return {
        ok: true,
        status: effectiveStatus,
        promoted: !autoWaitlisted && status === effectiveStatus,
      };
    },
    [user, eventId, myRsvp, load, probeCapacity],
  );

  const cancelRsvp = useCallback(async (): Promise<
    { ok: true; promoted_user_id?: string } | { error: string }
  > => {
    if (!myRsvp) return { ok: true };
    const wasGoing = myRsvp.status === "going";
    const myGuestCount = myGuests.length || 1;

    await supabase.from("event_guests").delete().eq("rsvp_id", myRsvp.id);
    const { error } = await supabase.from("event_rsvps").delete().eq("id", myRsvp.id);
    if (error) return { error: error.message };

    let promotedUserId: string | undefined;
    // Best-effort waitlist promotion. Only attempt when a "going" RSVP freed
    // up seats and the event has a capacity. RLS will silently block writes
    // by non-hosts on other users' rows — that's fine, the next host load or
    // server-side cron can reconcile.
    if (wasGoing && event?.capacity != null && eventId) {
      const { data: head } = await supabase
        .from("event_rsvps")
        .select("id, user_id, created_at")
        .eq("event_id", eventId)
        .eq("status", "waitlist")
        .order("created_at", { ascending: true })
        .limit(1)
        .maybeSingle();
      const headRow = head as { id: string; user_id: string } | null;
      if (headRow) {
        const { error: promoteErr } = await supabase
          .from("event_rsvps")
          .update({ status: "going" })
          .eq("id", headRow.id);
        if (!promoteErr) {
          // Mint the primary guest row for the promoted attendee if missing.
          const { data: existing } = await supabase
            .from("event_guests")
            .select("id")
            .eq("rsvp_id", headRow.id)
            .limit(1);
          if (((existing as { id: string }[] | null) ?? []).length === 0) {
            await supabase
              .from("event_guests")
              .insert({ rsvp_id: headRow.id, guest_name: null, checked_in: false } as any);
          }
          promotedUserId = headRow.user_id;
        }
      }
    }

    await load();
    return { ok: true, promoted_user_id: promotedUserId };
    // myGuestCount preserved in closure for future analytics if needed.
    void myGuestCount;
  }, [myRsvp, myGuests.length, event, eventId, load]);

  const setGuestCheckedIn = useCallback(
    async (guestId: string, checked: boolean) => {
      const { error } = await supabase
        .from("event_guests")
        .update({
          checked_in: checked,
          checked_in_at: checked ? new Date().toISOString() : null,
        } as any)
        .eq("id", guestId);
      if (error) return { error: error.message };
      await load();
      return { ok: true as const };
    },
    [load],
  );

  const isHost = !!user && !!event && event.host_user_id === user.id;

  // Host-only capacity stats derived from already-loaded data.
  const capacityStats = useMemo(() => {
    if (!event) return null;
    const goingRsvpIds = new Set(allRsvps.filter((r) => r.status === "going").map((r) => r.id));
    const ticketsGoing = allGuests.filter((g) => goingRsvpIds.has(g.rsvp_id ?? ""))
      .length || allRsvps.filter((r) => r.status === "going").length;
    const waitlistCount = allRsvps.filter((r) => r.status === "waitlist").length;
    const seatsLeft =
      event.capacity == null ? null : Math.max(0, event.capacity - ticketsGoing);
    return {
      capacity: event.capacity,
      tickets_going: ticketsGoing,
      seats_left: seatsLeft,
      waitlist_count: waitlistCount,
      is_full: event.capacity != null && (seatsLeft ?? 0) === 0,
    };
  }, [event, allRsvps, allGuests]);

  return {
    event,
    isHost,
    myRsvp,
    myGuests,
    allRsvps,
    allGuests,
    loading,
    error,
    reload: load,
    rsvp,
    cancelRsvp,
    setGuestCheckedIn,
    probeCapacity,
    capacityStats,
  };
};
